import { memo, useCallback, useState } from 'react'
import { WIDE_DESKTOP_MIN_WIDTH } from 'constants/breakpoints'
import { sortBy } from 'lodash-es'
import {
  PngExportProvider,
  type PieChartLegendClickData
} from '@jana-technology/platform-ui/charts'
import { useMediaQuery } from '@mui/material'

import {
  IQRAGraphCsvExportParams,
  IQRARiskByCategoryGraphApiResult,
  IQRARiskBySystemGraphApiResult
} from '_/src/app/services/api/qra/graphs'
import { RiskThresholdLevelName } from 'app/services/api/acceptanceCategories'
import { LengthOfPipe, RiskBySystem } from 'common/components/charts'
import { selectDataLabel } from 'common/utils/chart/chartUtils'

import {
  ILocalFilter,
  RiskUnitType
} from 'features/RiskManagement/Components/LocalFilter'

import {
  IChartData,
  IQRAChartBaseObject,
  IQRAChartObjects,
  IQRARiskByRouteChartObject,
  IQRARiskForecastChartObject,
  IQRAThreatChartObject,
  QRAChartItems,
  QRAGraphType,
  QRARiskForecastChartItems
} from '.'
import { ExportableGenericDonutChart } from './Components/ExportableGenericDonutChart'
import GraphContainerCard from './Components/GraphContainerCard'
import {
  getLRGraphData,
  getStandardChartCVPairs,
  getThreatCategoryChartCVPairs
} from './graphUtils'
import ConsequencesChart from './Implementations/ConsequencesChart'
import LinearReferenceChart from './Implementations/GraphLinearReference'
import NoDataChart from './Implementations/NoDataChart'
import RiskByLineChart from './Implementations/RiskByLineChart'
import RiskForecastChart, {
  IRiskForecastChartYearSeriesItem
} from './Implementations/RiskForecastChart'
import type { RiskForecastGraphs } from './Implementations/RiskForecastGraphs'
import ThreatsChart from './Implementations/ThreatsChart'

const riskSignificanceCategoryOrder = [
  RiskThresholdLevelName.LowRisk,
  RiskThresholdLevelName.MediumLowRisk,
  RiskThresholdLevelName.MediumRisk,
  RiskThresholdLevelName.MediumHighRisk,
  RiskThresholdLevelName.HighRisk
]

const isPieChartCleared = ({ activeSliceCount }: PieChartLegendClickData) =>
  activeSliceCount === 0

const isBaselinePieName = (name?: string) => {
  if (name == null) {
    return true
  }

  const normalizedName = name.toLowerCase()

  return (
    normalizedName.startsWith('total') || normalizedName.startsWith('baseline')
  )
}

const getActiveLegendCategoryIds = ({
  visiblePie
}: PieChartLegendClickData): number[] =>
  (visiblePie.activeData ?? visiblePie.data).flatMap((datum) => {
    const { categoryId } = datum as { categoryId?: unknown }

    return typeof categoryId === 'number' ? [categoryId] : []
  })

const hasComparisonPieSeries = (
  data?: IQRARiskByCategoryGraphApiResult | IQRARiskBySystemGraphApiResult
) =>
  data?.categoriesResult.some(({ values }) =>
    values?.some(({ key }) => !isBaselinePieName(key))
  ) ?? false

const getComparisonSeriesCategoryIds = ({
  activeCategoryIds,
  categoryIds,
  hasComparisonSeries
}: {
  activeCategoryIds?: number[]
  categoryIds?: number[]
  hasComparisonSeries: boolean
}): number[] | undefined =>
  hasComparisonSeries ? (activeCategoryIds ?? categoryIds) : undefined

const isPieChartExportDisabled = ({
  hasWisSeries,
  isBaselineCleared,
  isWisCleared
}: {
  hasWisSeries: boolean
  isBaselineCleared: boolean
  isWisCleared: boolean
}) => isBaselineCleared && (!hasWisSeries || isWisCleared)

const setPieSeriesCleared = (
  data: PieChartLegendClickData,
  setIsBaselineCleared: (isCleared: boolean) => void,
  setIsWisCleared: (isCleared: boolean) => void
) => {
  const setIsCleared = isBaselinePieName(data.visiblePie.name)
    ? setIsBaselineCleared
    : setIsWisCleared

  setIsCleared(isPieChartCleared(data))
}

const isRiskByRouteChart = (
  chart?: IQRAChartBaseObject
): chart is IQRARiskByRouteChartObject =>
  chart != null &&
  'routes' in chart &&
  Array.isArray(chart.routes) &&
  'groupingConfig' in chart &&
  Array.isArray(chart.groupingConfig)

type GetGraphComponentByIdProps = {
  graphType: string
  showLoading: boolean
  graphLoading?: boolean
  charts: IQRAChartObjects
  riskByPipelineLengthGraphData?: IQRARiskByCategoryGraphApiResult
  riskBySystemGraphData?: IQRARiskBySystemGraphApiResult
  riskUnit?: RiskUnitType
  localFilter: ILocalFilter
  qraBasePath?: string
  itemsFromGraphType?: Record<string, () => QRAChartItems[] | undefined>
  csvExportParams?: IQRAGraphCsvExportParams
  onRiskForecastGraphTypeChange?: (graphType: RiskForecastGraphs) => void
}

const GraphComponentByIdComponent = ({
  graphType,
  showLoading,
  graphLoading,
  charts,
  riskByPipelineLengthGraphData,
  riskBySystemGraphData,
  riskUnit,
  localFilter,
  qraBasePath,
  itemsFromGraphType,
  csvExportParams,
  onRiskForecastGraphTypeChange
}: GetGraphComponentByIdProps) => {
  const isWideDesktop = useMediaQuery(`(min-width:${WIDE_DESKTOP_MIN_WIDTH}px)`)
  const isGraphLoading = graphLoading ?? showLoading
  const [isBaselinePipelineLengthCleared, setIsBaselinePipelineLengthCleared] =
    useState(false)
  const [isWisPipelineLengthCleared, setIsWisPipelineLengthCleared] =
    useState(false)
  const [
    activeBaselineAcceptanceCategory,
    setActiveBaselineAcceptanceCategory
  ] = useState<number[]>()
  const [activeWisAcceptanceCategory, setActiveWisAcceptanceCategory] =
    useState<number[]>()

  const [activeBaselineSystemIds, setActiveBaselineSystemIds] =
    useState<number[]>()
  const [activeScenarioSystemIds, setActiveScenarioSystemIds] =
    useState<number[]>()

  const [isBaselineRiskBySystemCleared, setIsBaselineRiskBySystemCleared] =
    useState(false)
  const [isWisRiskBySystemCleared, setIsWisRiskBySystemCleared] =
    useState(false)

  const hasWisPipelineLengthSeries = hasComparisonPieSeries(
    riskByPipelineLengthGraphData
  )

  const hasWisRiskBySystemSeries = hasComparisonPieSeries(riskBySystemGraphData)

  const isPipelineLengthCleared = isPieChartExportDisabled({
    hasWisSeries: hasWisPipelineLengthSeries,
    isBaselineCleared: isBaselinePipelineLengthCleared,
    isWisCleared: isWisPipelineLengthCleared
  })

  const isRiskBySystemCleared = isPieChartExportDisabled({
    hasWisSeries: hasWisRiskBySystemSeries,
    isBaselineCleared: isBaselineRiskBySystemCleared,
    isWisCleared: isWisRiskBySystemCleared
  })

  const onPipelineLengthLegendClick = useCallback(
    (data: PieChartLegendClickData) => {
      setPieSeriesCleared(
        data,
        setIsBaselinePipelineLengthCleared,
        setIsWisPipelineLengthCleared
      )

      if (isBaselinePieName(data.visiblePie.name)) {
        setActiveBaselineAcceptanceCategory(getActiveLegendCategoryIds(data))
      } else {
        setActiveWisAcceptanceCategory(getActiveLegendCategoryIds(data))
      }
    },
    []
  )

  const onRiskBySystemLegendClick = useCallback(
    (data: PieChartLegendClickData) => {
      setPieSeriesCleared(
        data,
        setIsBaselineRiskBySystemCleared,
        setIsWisRiskBySystemCleared
      )

      if (isBaselinePieName(data.visiblePie.name)) {
        setActiveBaselineSystemIds(getActiveLegendCategoryIds(data))
      } else {
        setActiveScenarioSystemIds(getActiveLegendCategoryIds(data))
      }
    },
    []
  )

  switch (graphType) {
    case QRAGraphType.THREATS: {
      const threatData = getStandardChartCVPairs(charts, graphType)
      const threatCategoryData = getThreatCategoryChartCVPairs(
        charts,
        graphType
      )

      const { values, labels } = threatData

      const stateNames = Object.keys(values)

      if (!isGraphLoading && stateNames.length < 1) {
        return <NoDataChart graphType={graphType} />
      }

      const chart = charts[graphType] as IQRAThreatChartObject | undefined
      const groupingConfig = chart?.groupingConfig?.[0].key

      return (
        <PngExportProvider>
          <ThreatsChart
            showLoading={isGraphLoading}
            threatData={normalizeThreatChartData(
              threatData,
              stateNames,
              labels
            )}
            threatCategoryData={normalizeThreatChartData(
              threatCategoryData,
              Object.keys(threatCategoryData.values),
              threatCategoryData.labels
            )}
            csvExportParams={csvExportParams}
            groupingKey={groupingConfig}
          />
        </PngExportProvider>
      )
    }

    case QRAGraphType.CONSEQUENCES: {
      const consequenceData = getStandardChartCVPairs(
        charts,
        QRAGraphType.CONSEQUENCES
      )

      const { values, labels } = consequenceData

      const stateNames = Object.keys(values)

      if (!isGraphLoading && stateNames.length < 1) {
        return <NoDataChart graphType={graphType} />
      }

      return (
        <PngExportProvider>
          <ConsequencesChart
            showLoading={isGraphLoading}
            consequenceData={normalizeThreatChartData(
              consequenceData,
              stateNames,
              labels
            )}
            csvExportParams={csvExportParams}
          />
        </PngExportProvider>
      )
    }

    case QRAGraphType.RISK_BY_LINE: {
      const chart = charts[graphType]
      const routeChart: IQRARiskByRouteChartObject = isRiskByRouteChart(chart)
        ? chart
        : {
            graphType: QRAGraphType.RISK_BY_LINE,
            routes: [],
            groupingConfig: []
          }

      if (!isGraphLoading && routeChart.routes.length < 1) {
        return <NoDataChart graphType={graphType} />
      }

      return (
        <PngExportProvider>
          <RiskByLineChart
            showLoading={isGraphLoading}
            chart={routeChart}
            routeNavigationBasePath={
              qraBasePath == null ? undefined : `${qraBasePath}/line`
            }
            csvExportParams={csvExportParams}
          />
        </PngExportProvider>
      )
    }

    case QRAGraphType.LINEAR_REFERENCE: {
      const { XValues, seriesValues, threshold, seriesLabels, measureValues } =
        getLRGraphData(charts, graphType)

      const stateNames = Object.keys(seriesValues)

      if (!isGraphLoading && stateNames.length < 1) {
        return <NoDataChart graphType={graphType} />
      }

      const getLocalFilterRoutes = (currentLine: string) =>
        sortBy(localFilter.segments.filter((segs) => segs.name === currentLine))

      return (
        <LinearReferenceChart
          showLoading={isGraphLoading}
          XValues={XValues}
          s1Values={seriesValues[stateNames[0]] ?? []}
          s2Values={seriesValues[stateNames[1]] ?? []}
          s1Label={seriesLabels[stateNames[0]]}
          s2Label={seriesLabels[stateNames[1]]}
          YBarValues1={seriesValues[stateNames[0]]}
          YBarValues2={stateNames[1] == null ? [] : seriesValues[stateNames[1]]}
          // To-Do: threshold functionality needs to be updated
          threshold={threshold}
          measureValues={measureValues}
          getLocalFilterRoutes={getLocalFilterRoutes}
        />
      )
    }

    case QRAGraphType.RISK_FORECAST: {
      /**
       * NOTE: Special data structure and logic, no point in creating
       *       abstractions that is not reused anywhere else.
       */

      const chartObject = charts[graphType] as
        | IQRARiskForecastChartObject
        | undefined

      const years: number[] = []
      const baseSeriesPerYear: IRiskForecastChartYearSeriesItem[][] = []
      const compareSeriesPerYear: IRiskForecastChartYearSeriesItem[][] = []
      const stateNames: string[] = []
      const labels: string[] = []

      const getSubThreats = (
        members: QRARiskForecastChartItems['items'][number]['members'],
        stateKey: string
      ): IRiskForecastChartYearSeriesItem['subThreats'] =>
        members?.map((member) => ({
          key: member.key,
          label: member.label,
          value:
            member.values.find((value) => value.key === stateKey)?.value ?? 0
        }))

      let riskForecastItems = chartObject?.items

      if (itemsFromGraphType) {
        riskForecastItems = itemsFromGraphType[
          QRAGraphType.RISK_FORECAST
        ]() as QRARiskForecastChartItems[]
      }

      if (riskForecastItems) {
        for (const i of riskForecastItems) {
          for (const cur of i.items) {
            for (const categoryValue of cur.values) {
              if (!stateNames.includes(categoryValue.key)) {
                stateNames.push(categoryValue.key)
                labels.push(selectDataLabel(categoryValue))
              }
            }
          }
        }

        for (const { year, items } of riskForecastItems) {
          years.push(year)
          baseSeriesPerYear.push([])
          compareSeriesPerYear.push([])

          const yearIdx = years.length - 1

          for (const { name, values, members } of items) {
            const baselineValue = values.find(
              (v) => v.key.toLowerCase() === stateNames[0]?.toLocaleLowerCase()
            )

            const compareValue = values.find(
              (v) => v.key.toLowerCase() !== stateNames[0]?.toLocaleLowerCase()
            )

            if (baselineValue != null) {
              baseSeriesPerYear[yearIdx].push({
                riskType: name,
                value: baselineValue.value,
                subThreats: getSubThreats(members, baselineValue.key)
              })
            }

            if (compareValue != null) {
              compareSeriesPerYear[yearIdx].push({
                riskType: name,
                value: compareValue.value,
                subThreats: getSubThreats(members, compareValue.key)
              })
            }
          }
        }
      }

      if (!isGraphLoading && years.length < 1) {
        return <NoDataChart graphType={graphType} />
      }

      return (
        <PngExportProvider>
          <RiskForecastChart
            showLoading={isGraphLoading}
            years={years}
            baseSeriesPerYear={baseSeriesPerYear}
            compareSeriesPerYear={compareSeriesPerYear}
            s1Label={labels[0]}
            s2Label={labels[1]}
            isWideDesktop={isWideDesktop}
            onGraphTypeChange={onRiskForecastGraphTypeChange}
            csvExportParams={csvExportParams}
          />
        </PngExportProvider>
      )
    }

    case QRAGraphType.RISK_BY_SIGNIFICANCE_LEVEL: {
      const pipelineLengthAcceptanceCategoryIds =
        riskByPipelineLengthGraphData?.categoriesResult.map(({ id }) => id)

      return (
        <ExportableGenericDonutChart
          showLoading={isGraphLoading}
          title='Risk by Pipeline Length'
          graphType={QRAGraphType.RISK_BY_SIGNIFICANCE_LEVEL}
          exportFileNameBase='Risk_By_Pipeline_Length'
          csvExportParams={csvExportParams}
          data={riskByPipelineLengthGraphData}
          disableExportMenuItems={isPipelineLengthCleared}
          baselineAcceptanceCategory={
            activeBaselineAcceptanceCategory ??
            pipelineLengthAcceptanceCategoryIds
          }
          wisAcceptanceCategory={getComparisonSeriesCategoryIds({
            activeCategoryIds: activeWisAcceptanceCategory,
            categoryIds: pipelineLengthAcceptanceCategoryIds,
            hasComparisonSeries: hasWisPipelineLengthSeries
          })}
        >
          <LengthOfPipe
            data={riskByPipelineLengthGraphData}
            categoryOrder={riskSignificanceCategoryOrder}
            isCleared={isPipelineLengthCleared}
            rangeValueType={
              riskUnit === RiskUnitType.TotalRisk ? 'total' : 'density'
            }
            onLegendItemClick={onPipelineLengthLegendClick}
            title='Risk by Pipeline Length'
          />
        </ExportableGenericDonutChart>
      )
    }

    case QRAGraphType.RISK_BY_SYSTEM: {
      const riskBySystemIds = riskBySystemGraphData?.categoriesResult.map(
        ({ id }) => id
      )

      return (
        <ExportableGenericDonutChart
          showLoading={isGraphLoading}
          title='Risk by System'
          graphType={QRAGraphType.RISK_BY_SYSTEM}
          exportFileNameBase='Risk_By_System'
          csvExportParams={csvExportParams}
          data={riskBySystemGraphData}
          disableExportMenuItems={isRiskBySystemCleared}
          baselineSystemIds={activeBaselineSystemIds ?? riskBySystemIds}
          scenarioSystemIds={getComparisonSeriesCategoryIds({
            activeCategoryIds: activeScenarioSystemIds,
            categoryIds: riskBySystemIds,
            hasComparisonSeries: hasWisRiskBySystemSeries
          })}
        >
          <RiskBySystem
            data={riskBySystemGraphData}
            isCleared={isRiskBySystemCleared}
            rangeValueType={
              riskUnit === RiskUnitType.TotalRisk ? 'total' : 'density'
            }
            onLegendItemClick={onRiskBySystemLegendClick}
            title='Risk by System'
          />
        </ExportableGenericDonutChart>
      )
    }

    default:
      return (
        <GraphContainerCard
          title={graphType}
          optionsComponent={<div>component</div>}
        >
          Under Construction
        </GraphContainerCard>
      )
  }
}

export const GraphComponentById = memo(GraphComponentByIdComponent)

const normalizeThreatChartData = (
  chartData: IChartData,
  stateNames: string[],
  labels: Record<string, string>
): IChartData => {
  const [primaryStateName, secondaryStateName] = stateNames

  if (primaryStateName == null) {
    return chartData
  }

  return {
    ...chartData,
    labels: {
      [primaryStateName]: labels[primaryStateName],
      ...(secondaryStateName == null
        ? {}
        : { [secondaryStateName]: labels[secondaryStateName] })
    },
    values: {
      [primaryStateName]: chartData.values[primaryStateName] ?? [],
      ...(secondaryStateName == null
        ? {}
        : { [secondaryStateName]: chartData.values[secondaryStateName] ?? [] })
    }
  }
}
// #endregion
